import { useMutation } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { OnboardingData } from '../features/onboarding/OnboardingWizard';
import type { SceneWithShots, Shot } from '../types/database';
import defaultSoundtrackUrl from '../assets/Ambient-Hopeful.mp3';

const EDGE_FUNCTION_URL = import.meta.env.VITE_SUPABASE_URL + '/functions/v1';

interface GenerateVideoParams {
  projectId: string;
  onboardingData: OnboardingData;
  onStatusChange?: (status: string) => void;
}

interface GeneratedScene {
  name: string;
  shots: Array<{
    name: string;
    narration: string;
    durationSec: number;
    aiPrompt: string;
  }>;
}

interface GenerateVideoResult {
  scenes: SceneWithShots[];
  projectId: string;
}

// Generate complete video with AI - script, images, and structure
export function useGenerateVideo() {
  return useMutation({
    mutationFn: async ({ projectId, onboardingData, onStatusChange }: GenerateVideoParams): Promise<GenerateVideoResult> => {
      const updateStatus = (msg: string) => onStatusChange?.(msg);
      let generatedScenes: GeneratedScene[] = [];
      
      // Step 1: Generate script using AI Edge Function
      updateStatus('Generiranje scenarija...');
      try {
        const { data: scriptData, error: scriptError } = await supabase.functions.invoke('generate-full-script', {
          body: { onboardingData },
        });

        if (scriptError) {
          console.error('Edge Function failed:', scriptError);
          throw new Error(`Failed to generate script: ${scriptError.message}`);
        }
        
        generatedScenes = scriptData.scenes || [];
      } catch (error) {
        console.error('Edge Function error:', error);
        throw new Error('Failed to generate script: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
      
      if (generatedScenes.length === 0) {
        throw new Error('No scenes generated');
      }

      // Step 2: Create scenes and shots in database
      const scenesWithShots: SceneWithShots[] = [];
      
      for (let sceneIndex = 0; sceneIndex < generatedScenes.length; sceneIndex++) {
        const generatedScene = generatedScenes[sceneIndex];
        
        updateStatus(`Kreiranje scene ${sceneIndex + 1} od ${generatedScenes.length}: ${generatedScene.name}`);
        
        // DEBUG: Log scene creation attempt
        console.log(`[useGenerateVideo] Creating scene ${sceneIndex}:`, {
          projectId,
          sceneName: generatedScene.name,
          generatedScene,
        });
        
        // Create scene in database
        const { data: sceneData, error: sceneError } = await supabase
          .from('scenes')
          .insert({
            project_id: projectId,
            order_index: sceneIndex,
            name: generatedScene.name,
            status: 'pending',
          })
          .select()
          .single();

        // DEBUG: Log scene creation result
        console.log(`[useGenerateVideo] Scene creation result:`, {
          sceneData,
          sceneError,
          sceneDataId: sceneData?.id,
        });

        if (sceneError) {
          console.error('Scene creation error:', sceneError);
          throw new Error(`Failed to create scene: ${generatedScene.name}`);
        }

        if (!sceneData || !sceneData.id) {
          console.error('[useGenerateVideo] Scene data is null or missing id:', sceneData);
          throw new Error(`Scene creation returned invalid data for: ${generatedScene.name}`);
        }

        // Create shots for this scene
        const shots: Shot[] = [];
        
        for (let shotIndex = 0; shotIndex < generatedScene.shots.length; shotIndex++) {
          const generatedShot = generatedScene.shots[shotIndex];
          
          // Generate image for this shot using AI
          updateStatus(`Generiranje slike za kadar ${shotIndex + 1} od ${generatedScene.shots.length} (scena ${sceneIndex + 1})...`);
          let mediaUrl = 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80';
          let thumbnailUrl = mediaUrl;
          
          try {
            const { data: imageData, error: imageError } = await supabase.functions.invoke('generate-image', {
              body: { 
                prompt: generatedShot.aiPrompt,
                aspectRatio: '16:9'
              },
            });
            
            if (imageError) {
              console.warn('Image generation error:', imageError);
              throw imageError;
            }
            
            if (imageData?.finalImageUrl) {
              mediaUrl = imageData.finalImageUrl;
              thumbnailUrl = mediaUrl;
            }
          } catch (imageError) {
            console.warn('Image generation failed, using fallback:', imageError);
            mediaUrl = getRelevantImageUrl(generatedShot.aiPrompt);
            thumbnailUrl = mediaUrl;
          }

          // Create shot in database
          const { data: shotData, error: shotError } = await supabase
            .from('shots')
            .insert({
              scene_id: sceneData.id,
              order_index: shotIndex,
              name: generatedShot.name,
              narration_text: generatedShot.narration,
              duration_sec: generatedShot.durationSec,
              visual_type: 'image',
              media_url: mediaUrl,
              ai_prompt: generatedShot.aiPrompt,
              thumbnail_url: thumbnailUrl,
            })
            .select()
            .single();

          if (shotError) {
            console.error('Shot creation error:', shotError);
            throw new Error(`Failed to create shot: ${generatedShot.name}`);
          }

          shots.push(shotData as Shot);
        }

        scenesWithShots.push({
          ...sceneData,
          shots,
        });
      }

      // Step 3: Handle background music based on mode
      if (onboardingData.backgroundMusicMode === 'ai_generated') {
        updateStatus('Generiranje pozadinske muzike...');
        try {
          await supabase.functions.invoke('generate-background-music', {
            body: {
              projectId,
              genre: onboardingData.musicGenre,
              duration: onboardingData.targetDurationMin * 60,
              tone: onboardingData.tone,
            },
          });
        } catch (musicError) {
          console.warn('Background music generation failed:', musicError);
          // Don't fail the whole operation if music fails
        }
      } else if (onboardingData.backgroundMusicMode === 'default_soundtrack') {
        updateStatus('Dodavanje default soundtracka...');
        try {
          await supabase
            .from('background_music_tracks')
            .insert({
              project_id: projectId,
              name: 'Ambient-Hopeful',
              source_type: 'upload',
              audio_url: defaultSoundtrackUrl,
              duration_sec: 0,
              genre: 'ambient',
              mood: 'hopeful',
              is_active: true,
              volume_level: 0.5,
              order_index: 0,
            });
        } catch (musicError) {
          console.warn('Default soundtrack insertion failed:', musicError);
        }
      }

      updateStatus('Završavanje...');
      return {
        scenes: scenesWithShots,
        projectId,
      };
    },
  });
}

// Helper function to get relevant image URL based on prompt
function getRelevantImageUrl(prompt: string): string {
  const promptLower = prompt.toLowerCase();
  
  // Croatian/Adriatic themed images
  if (promptLower.includes('zadar') || promptLower.includes('croatia') || promptLower.includes('adriatic')) {
    return 'https://images.unsplash.com/photo-1544919982-b61976f0ba43?auto=format&fit=crop&w=1200&q=80';
  }
  
  if (promptLower.includes('beach') || promptLower.includes('sea') || promptLower.includes('ocean')) {
    return 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80';
  }
  
  if (promptLower.includes('sunset') || promptLower.includes('sunrise')) {
    return 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1200&q=80';
  }
  
  if (promptLower.includes('mountain') || promptLower.includes('nature')) {
    return 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1200&q=80';
  }
  
  if (promptLower.includes('city') || promptLower.includes('urban')) {
    return 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?auto=format&fit=crop&w=1200&q=80';
  }
  
  if (promptLower.includes('food') || promptLower.includes('restaurant')) {
    return 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1200&q=80';
  }
  
  // Default fallback
  return 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80';
}
